// ─────────────────────────────────────────────────────────────────────────────
// BandwidthChart.js — throughput over the selected range, in the wide bottom
// card. Matches the Figma frame: horizontal-only dashed grid (7 lines), 7 evenly
// spaced axis labels per side, a #22D3EE curve, the green (#00FFA3) hover marker
// shared with ActivityChart, and a time-stamped hover card.
//
// Like ActivityChart, the viewBox tracks the card's measured pixel width so the
// SVG is never stretched.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useRef } from "react";
import { useElementSize } from "../../../shared/hooks";
import { monotoneXPath, areaPath, presetCeiling } from "../../../shared/lib/chartMath";
import { ReactComponent as SessionIcon } from "../../../assets/icons/session.svg";
import { ReactComponent as TimeIcon }    from "../../../assets/icons/time.svg";
import styles from "./BandwidthChart.module.css";

const BW = { H: 380, left: 52, rightPad: 16, top: 14, bottomPad: 30 };

const CEILING_PRESETS = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];

// Rate units, smallest first — the peak picks the one that keeps the axis in
// legible whole numbers rather than printing 0.004 Gbps.
const UNITS = [
  { limit: 1e6,  unit: "Kbps", divider: 1e3  },
  { limit: 1e9,  unit: "Mbps", divider: 1e6  },
  { limit: 1e12, unit: "Gbps", divider: 1e9  },
  { limit: Infinity, unit: "Tbps", divider: 1e12 },
];

/**
 * @param {object}   props
 * @param {string[]} props.labels    x-axis labels, one per value
 * @param {number[]} props.values    throughput in bits/sec
 * @param {boolean}  [props.isHourly] append ":00" to the labels
 */
export default function BandwidthChart({ labels, values, isHourly = true }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [wrapRef, { width }] = useElementSize({ width: 760, height: BW.H });
  const svgRef = useRef();

  const W = width;
  const right  = W - BW.rightPad;
  const bottom = BW.H - BW.bottomPad;

  const peak = Math.max(...values, 0);
  const { unit, divider } = UNITS.find((u) => peak < u.limit) ?? UNITS[1];

  const scaled = values.map((v) => v / divider);
  const maxV = presetCeiling(Math.max(...scaled, 0), CEILING_PRESETS);
  const n = scaled.length;

  const xs = scaled.map((_, i) => BW.left + (n > 1 ? (i / (n - 1)) * (right - BW.left) : 0));
  const ys = scaled.map((v) => bottom - (v / (maxV || 1)) * (bottom - BW.top));

  const line = monotoneXPath(xs, ys);
  const area = areaPath(line, xs, bottom);

  const yTicks = [0, 1, 2, 3, 4, 5, 6].map((i) => BW.top + (i / 6) * (bottom - BW.top));
  const xTickIndices = [0, 1, 2, 3, 4, 5, 6].map((i) => Math.round((i / 6) * (n - 1)));

  const axisLabel = (i) => `${labels[i]}${isHourly ? ":00" : ""}`;

  const handleMouseMove = (e) => {
    if (n < 2) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((svgX - BW.left) / (right - BW.left)) * (n - 1));
    setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
  };

  const hover = hoverIdx !== null ? { x: xs[hoverIdx], y: ys[hoverIdx] } : null;

  return (
    <div className={styles.chart} ref={wrapRef}>
      <svg
        ref={svgRef} viewBox={`0 0 ${W} ${BW.H}`} preserveAspectRatio="none" className={styles.svg}
        onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id="bwg" x1={BW.left} y1={BW.top} x2={BW.left} y2={bottom} gradientUnits="userSpaceOnUse">
            <stop offset="5%"  stopColor="#22D3EE" stopOpacity="0.3" />
            <stop offset="95%" stopColor="#22D3EE" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Grid — horizontal only, dashed, muted slate */}
        {yTicks.map((y, i) => (
          <line key={`y-${i}`} x1={BW.left} y1={y} x2={right} y2={y} stroke="#1F2937" strokeDasharray="3 3" />
        ))}

        {/* Y-axis labels — the bottom tick carries the unit */}
        {yTicks.map((y, i) => (
          <text key={`yt-${i}`} x={BW.left - 9} y={y + 4} textAnchor="end" fontSize="12" fill="#6B7280">
            {Math.round(maxV * (1 - i / 6))}{i === 0 ? ` ${unit}` : ""}
          </text>
        ))}

        {/* X-axis labels */}
        {xTickIndices.map((idx, i) => (
          <text key={`xt-${i}`} x={xs[idx]} y={BW.H - 8} textAnchor="middle" fontSize="12" fill="#6B7280">
            {axisLabel(idx)}
          </text>
        ))}

        {/* Area + line */}
        {line && (
          <>
            <path d={area} fill="url(#bwg)" />
            <path d={line} fill="none" stroke="#22D3EE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}

        {/* Hover crosshair + marker — green, shared style with ActivityChart */}
        {hover && (
          <>
            <line x1={hover.x} y1={BW.top} x2={hover.x} y2={bottom} stroke="white" strokeOpacity="0.16" />
            <circle cx={hover.x} cy={hover.y} r="5" fill="black" stroke="#00FFA3" strokeWidth="4" />
          </>
        )}
      </svg>

      {/* Hover card — icon + value/label + time badge */}
      {hover && (
        <div
          className={`${styles.hoverCard} ${hover.x > W / 2 ? styles.hoverCardFlip : ""}`}
          style={{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / BW.H) * 100}%` }}
        >
          <div className={styles.hoverIconBox}>
            <SessionIcon className={styles.hoverIcon} />
          </div>
          <div className={styles.hoverText}>
            <div className={styles.hoverValue}>
              {scaled[hoverIdx].toFixed(2)} <span>{unit}</span>
            </div>
            <div className={styles.hoverLabel}>Bandwidth</div>
          </div>
          <div className={styles.hoverTimeBadge}>
            <TimeIcon className={styles.timeIcon} />
            <span>{axisLabel(hoverIdx)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
